/**
 * Industry -> sector mapping, shared by every worker that rolls per-ticker rows
 * up to a sector. `company_profile` has no sector column, so industry is the
 * only classification we store and every rollup goes through this map.
 * Industries mapped to null are deliberately excluded (shell companies, N/A).
 */
export const INDUSTRY_TO_SECTOR_MAP = {
	// Technology
	'Software - Infrastructure': 'Technology',
	'Software - Application': 'Technology',
	'Computer Hardware': 'Technology',
	'Semiconductors': 'Technology',
	'Semiconductor Equipment & Materials': 'Technology',
	'Information Technology Services': 'Technology',
	'Electronic Components': 'Technology',
	'Scientific & Technical Instruments': 'Technology',
	'Tech': 'Technology',
	'Electronics & Computer Distribution': 'Technology',

	// Communications
	'Communication Equipment': 'Communications',
	'Telecom Services': 'Communications',
	'Internet Content & Information': 'Communications',
	'Advertising Agencies': 'Communications',
	'Broadcasting': 'Communications',
	'Interactive Media': 'Communications',
	'Electronic Gaming & Multimedia': 'Communications',

	// Healthcare
	'Biotechnology': 'Health Care',
	'Medical Devices': 'Health Care',
	'Medical Care Facilities': 'Health Care',
	'Medical Instruments & Supplies': 'Health Care',
	'Drug Manufacturers - Specialty & Generic': 'Health Care',
	'Drug Manufacturers - General': 'Health Care',
	'Healthcare Plans': 'Health Care',
	'Medical Distribution': 'Health Care',
	'Pharmaceutical Retailers': 'Health Care',
	'Diagnostics & Research': 'Health Care',
	'Health Information Services': 'Health Care',

	// Financials
	'Banks - Regional': 'Financials',
	'Banks - Diversified': 'Financials',
	'Insurance - Diversified': 'Financials',
	'Insurance - Specialty': 'Financials',
	'Asset Management': 'Financials',
	'Capital Markets': 'Financials',
	'Insurance - Life': 'Financials',
	'Insurance - Property & Casualty': 'Financials',
	'Financial Data & Stock Exchanges': 'Financials',
	'Insurance Brokers': 'Financials',
	'Credit Services': 'Financials',
	'Mortgage Finance': 'Financials',
	'Financial Conglomerates': 'Financials',
	'Insurance - Reinsurance': 'Financials',

	// Consumer Discretionary
	'Specialty Retail': 'Consumer Discretionary',
	'Consumer Electronics': 'Consumer Discretionary',
	'Internet Retail': 'Consumer Discretionary',
	'Apparel Retail': 'Consumer Discretionary',
	'Auto Parts': 'Consumer Discretionary',
	'Home Improvement Retail': 'Consumer Discretionary',
	'Auto Manufacturers': 'Consumer Discretionary',
	'Restaurants': 'Consumer Discretionary',
	'Travel Services': 'Consumer Discretionary',
	'Apparel Manufacturing': 'Consumer Discretionary',
	'Furnishings, Fixtures & Appliances': 'Consumer Discretionary',
	'Footwear & Accessories': 'Consumer Discretionary',
	'Gambling': 'Consumer Discretionary',
	'Department Stores': 'Consumer Discretionary',
	'Luxury Goods': 'Consumer Discretionary',
	'Auto & Truck Dealerships': 'Consumer Discretionary',
	'Recreational Vehicles': 'Consumer Discretionary',
	'Leisure': 'Consumer Discretionary',

	// Consumer Staples
	'Beverages - Brewers': 'Consumer Staples',
	'Beverages - Non-Alcoholic': 'Consumer Staples',
	'Packaged Foods': 'Consumer Staples',
	'Grocery Stores': 'Consumer Staples',
	'Household & Personal Products': 'Consumer Staples',
	'Food Distribution': 'Consumer Staples',
	'Confectioners': 'Consumer Staples',
	'Beverages - Wineries & Distilleries': 'Consumer Staples',
	'Tobacco': 'Consumer Staples',

	// Energy
	'Oil & Gas Equipment & Services': 'Energy',
	'Oil & Gas E&P': 'Energy',
	'Oil & Gas Drilling': 'Energy',
	'Oil & Gas Integrated': 'Energy',
	'Oil & Gas Refining & Marketing': 'Energy',
	'Oil & Gas Midstream': 'Energy',
	'Independent Oil & Gas': 'Energy',

	// Materials
	'Specialty Chemicals': 'Materials',
	'Chemicals': 'Materials',
	'Other Industrial Metals & Mining': 'Materials',
	'Gold': 'Materials',
	'Aluminum': 'Materials',
	'Copper': 'Materials',
	'Other Precious Metals & Mining': 'Materials',
	'Paper & Paper Products': 'Materials',
	'Steel': 'Materials',
	'Agricultural Inputs': 'Materials',
	'Silver': 'Materials',
	'Coking Coal': 'Materials',
	'Thermal Coal': 'Materials',
	'Uranium': 'Materials',

	// Industrials
	'Building Products & Equipment': 'Industrials',
	'Specialty Business Services': 'Industrials',
	'Engineering & Construction': 'Industrials',
	'Airlines': 'Industrials',
	'Marine Shipping': 'Industrials',
	'Airports & Air Services': 'Industrials',
	'Trucking': 'Industrials',
	'Electrical Equipment & Parts': 'Industrials',
	'Aerospace & Defense': 'Industrials',
	'Industrial Distribution': 'Industrials',
	'Security & Protection Services': 'Industrials',
	'Integrated Freight & Logistics': 'Industrials',
	'Waste Management': 'Industrials',
	'Farm & Heavy Construction Machinery': 'Industrials',
	'Pollution & Treatment Controls': 'Industrials',
	'Specialty Industrial Machinery': 'Industrials',
	'Railroads': 'Industrials',
	'Business Equipment & Supplies': 'Industrials',
	'Infrastructure Operations': 'Industrials',
	'Rental & Leasing Services': 'Industrials',
	'Staffing & Employment Services': 'Industrials',
	'Consulting Services': 'Industrials',

	// Real Estate
	'REIT - Mortgage': 'Real Estate',
	'REIT - Residential': 'Real Estate',
	'Real Estate Services': 'Real Estate',
	'Real Estate - Development': 'Real Estate',
	'REIT - Office': 'Real Estate',
	'REIT - Hotel & Motel': 'Real Estate',
	'REIT - Healthcare Facilities': 'Real Estate',
	'REIT - Industrial': 'Real Estate',
	'REIT - Retail': 'Real Estate',
	'REIT - Specialty': 'Real Estate',
	'REIT - Diversified': 'Real Estate',
	'Real Estate - Diversified': 'Real Estate',

	// Utilities
	'Utilities - Renewable': 'Utilities',
	'Utilities - Diversified': 'Utilities',
	'Utilities - Regulated Electric': 'Utilities',
	'Utilities - Regulated Gas': 'Utilities',
	'Utilities - Independent Power Producers': 'Utilities',
	'Utilities - Regulated Water': 'Utilities',
	'Solar': 'Utilities',

	// Materials - additional
	'Packaging & Containers': 'Materials',
	'Metal Fabrication': 'Materials',
	'Building Materials': 'Materials',
	'Lumber & Wood Production': 'Materials',

	// Consumer Discretionary - additional
	'Resorts & Casinos': 'Consumer Discretionary',
	'Residential Construction': 'Consumer Discretionary',
	'Lodging': 'Consumer Discretionary',
	'Personal Services': 'Consumer Discretionary',
	'Textile Manufacturing': 'Consumer Discretionary',

	// Consumer Staples - additional
	'Farm Products': 'Consumer Staples',
	'Discount Stores': 'Consumer Staples',

	// Communications - additional
	'Entertainment': 'Communications',
	'Publishing': 'Communications',

	// Industrials - additional
	'Education & Training Services': 'Industrials',
	'Tools & Accessories': 'Industrials',

	// Other/Misc
	'Conglomerates': 'Industrials',
	'Shell Companies': null,
	'N/A': null
};

/**
 * The sector names the map resolves to, in display order. Anything the map
 * sends to null (shell companies, N/A) belongs to no sector.
 */
export const SECTORS = [
	'Communications',
	'Consumer Discretionary',
	'Consumer Staples',
	'Energy',
	'Financials',
	'Health Care',
	'Industrials',
	'Materials',
	'Real Estate',
	'Technology',
	'Utilities'
];

/**
 * @param {string} industry - raw `company_profile.industry` value
 * @returns {string|null} its sector, or null when unmapped or deliberately excluded
 */
export function sectorForIndustry(industry) {
	if (typeof industry !== 'string') return null;
	const key = industry.trim();
	return Object.prototype.hasOwnProperty.call(INDUSTRY_TO_SECTOR_MAP, key) ? INDUSTRY_TO_SECTOR_MAP[key] : null;
}

/**
 * Expand sector names into every industry that rolls up to one of them.
 * Unknown sector names contribute nothing.
 * @param {string[]} sectors
 * @returns {string[]} industry names
 */
export function industriesForSectors(sectors) {
	if (!Array.isArray(sectors) || sectors.length === 0) return [];
	const wanted = new Set(sectors);
	return Object.keys(INDUSTRY_TO_SECTOR_MAP).filter((industry) => wanted.has(INDUSTRY_TO_SECTOR_MAP[industry]));
}

/**
 * Curated sector-concentrated ETFs, ticker -> sector, so a sector list can carry the
 * funds that track it next to its stocks. Only broad sector funds and the best-known
 * industry funds are here: every other ETF, and every mutual fund or closed-end fund,
 * is deliberately left out of the sector lists, because a thematic or multi-sector
 * fund has no single sector to belong to.
 */
export const ETF_SECTOR_MAP = {
	// SPDR Select Sector funds
	XLK: 'Technology',
	XLF: 'Financials',
	XLE: 'Energy',
	XLV: 'Health Care',
	XLY: 'Consumer Discretionary',
	XLP: 'Consumer Staples',
	XLI: 'Industrials',
	XLB: 'Materials',
	XLU: 'Utilities',
	XLRE: 'Real Estate',
	XLC: 'Communications',

	// Vanguard sector funds
	VGT: 'Technology',
	VFH: 'Financials',
	VDE: 'Energy',
	VHT: 'Health Care',
	VCR: 'Consumer Discretionary',
	VDC: 'Consumer Staples',
	VIS: 'Industrials',
	VAW: 'Materials',
	VPU: 'Utilities',
	VNQ: 'Real Estate',
	VOX: 'Communications',

	// Industry funds
	SMH: 'Technology',
	SOXX: 'Technology',
	IGV: 'Technology',
	KRE: 'Financials',
	KBE: 'Financials',
	IAT: 'Financials',
	XBI: 'Health Care',
	IBB: 'Health Care',
	IHI: 'Health Care',
	XOP: 'Energy',
	OIH: 'Energy',
	ITB: 'Consumer Discretionary',
	XHB: 'Consumer Discretionary',
	XRT: 'Consumer Discretionary',
	IYR: 'Real Estate'
};

/**
 * @param {string} sector
 * @returns {string[]} the curated ETF tickers for that sector, in map order
 */
export function etfsForSector(sector) {
	return Object.keys(ETF_SECTOR_MAP).filter((ticker) => ETF_SECTOR_MAP[ticker] === sector);
}

/**
 * Lowercase, `&` read as "and", every other run of non-alphanumerics collapsed to one
 * underscore, no leading or trailing underscore: 'Oil & Gas E&P' -> 'oil_and_gas_e_and_p'.
 */
export function slugify(name) {
	return String(name)
		.toLowerCase()
		.replace(/&/g, ' and ')
		.replace(/[^a-z0-9]+/g, '_')
		.replace(/^_+|_+$/g, '');
}

/**
 * The screener_data list id of a sector's "All <Sector>" list, which is also its
 * /list/<id> URL. Pure, so the UI can link a sector mention to its list.
 * @param {string} sector - one of SECTORS
 * @returns {string} e.g. 'sector_health_care'
 */
export function sectorListId(sector) {
	return `sector_${slugify(sector)}`;
}

/**
 * The screener_data list id of an industry's list, also its /list/<id> URL.
 * @param {string} industry - a key of INDUSTRY_TO_SECTOR_MAP
 * @returns {string} e.g. 'industry_software_infrastructure'
 */
export function industryListId(industry) {
	return `industry_${slugify(industry)}`;
}

/**
 * The screener_categories id holding a sector's lists in the menu. Distinct from
 * sectorListId, so a category never shares an id with a list.
 * @param {string} sector - one of SECTORS
 * @returns {string} e.g. 'sectors_health_care'
 */
export function sectorCategoryId(sector) {
	return `sectors_${slugify(sector)}`;
}
